import React, { useState, useCallback } from 'react';
import type { Vehicle, Driver, Id } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useVehicleUpdateApi } from '@hero/api-hooks';
import { toast } from 'sonner';
import { ModalForm } from '../../../app/Modal';
import { formatPlateForDisplay } from '../../../app/Select/SelectAsync/selectorEntityRowUtils';
import { DriverSelector, type DriverSelectionValue } from '../../Driver/Selector';

export interface VehicleAssignDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Vehicle;
}

export function VehicleAssignDriverModal({
  isOpen,
  onClose,
  vehicle,
}: VehicleAssignDriverModalProps) {
  const { t } = useTranslation(['m-vehicle', 'c-main']);
  const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>(undefined);

  const currentDriverId = vehicle.driver?.driverId;
  const effectiveDriverId: Id | undefined = selectedDriver?._id ?? currentDriverId;
  const selectorValue: DriverSelectionValue = selectedDriver ?? currentDriverId;

  const {
    mutate: updateVehicle,
    isPending,
    isSuccess,
  } = useVehicleUpdateApi({
    onSuccess: () => {
      toast.success(t('toast.success.driver_assigned'));
      onClose();
      setSelectedDriver(undefined);
    },
    onError: (error: Error) => {
      toast.error(t('toast.error.driver_assign', { message: error.message }));
    },
  });

  const handleSave = useCallback(() => {
    if (!effectiveDriverId) return;
    updateVehicle({
      _id: vehicle._id,
      driverId: String(effectiveDriverId),
    } as any);
  }, [effectiveDriverId, vehicle._id, updateVehicle]);

  const handleClose = () => {
    setSelectedDriver(undefined);
    onClose();
  };

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={handleClose}
      onSave={handleSave}
      title={t('modal.assign_driver_title')}
      description={t('modal.assign_driver_description', {
        plate: formatPlateForDisplay(vehicle.registration.plate) || vehicle.registration.plate,
      })}
      isLoading={isPending}
      isSuccess={isSuccess}
      size="md"
      saveText={t('modal.assign')}
      disableSave={!effectiveDriverId}>
      <DriverSelector
        selectedDrivers={selectorValue}
        onDriversChange={(drivers: Driver[] | undefined) => setSelectedDriver(drivers?.[0])}
        label={t('modal.select_driver_placeholder')}
      />
    </ModalForm>
  );
}
